import { computed } from "vue";
import { DateTime } from "luxon";
import { fuzzyDateParse } from "@loanlink-nl/migration";
import { storeToRefs } from "pinia";
import { useDossierDataStore } from "@/store/useDossierDataStore";

function isYearOnly(value?: string): boolean {
	return !!value && /^\d{4}$/.test(value);
}

export function useCalculationDate() {
	const dossierDataStore = useDossierDataStore();
	const { data } = storeToRefs(dossierDataStore);

	const calculationDateIsYearOnly = computed(() => {
		return isYearOnly(data.value.financieringsOpzet?.rekendatum);
	});

	const calculationDate = computed(() => {
		const rekendatum = data.value.financieringsOpzet?.rekendatum ?? "";

		// Handle year-only format (4 digits)
		if (isYearOnly(rekendatum)) {
			const year = parseInt(rekendatum, 10);
			const currentYear = DateTime.now().year;

			if (year === currentYear) {
				// Current year: return today's date
				return DateTime.now();
			}
			if (year < currentYear) {
				// Previous year: return last day of that year
				return DateTime.fromObject({ year, month: 12, day: 31 });
			}
			// Future year: return first day of that year
			return DateTime.fromObject({ year, month: 1, day: 1 });
		}

		const enteredDate = fuzzyDateParse(rekendatum);
		if (!enteredDate.isValid) {
			return DateTime.now();
		}

		return enteredDate;
	});

	const calculationDateString = computed((): string => {
		return calculationDate.value.toISODate() ?? "";
	});

	const passingDate = computed(() => {
		const enteredDate = fuzzyDateParse(
			data.value.financieringsOpzet?.passeerdatum ?? "",
		);
		if (!enteredDate.isValid) {
			return DateTime.now().plus({ months: 2 }).startOf("month");
		}

		return enteredDate;
	});

	const passingDateString = computed(() => {
		return passingDate.value.toISODate();
	});

	return {
		calculationDate,
		calculationDateString,
		calculationDateIsYearOnly,
		passingDate,
		passingDateString,
	};
}
